import { Fragment, useMemo, useState, useEffect, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationIcon, XIcon } from '@heroicons/react/solid'
import { useTranslation } from 'react-i18next'
import { Button, Loader } from '../../components'
import { unassignCycleCounts } from '../../services/cycleCountServices'
import { chunk } from './events/collectUnassignedIds'

const T_PREFIX = 'cycle_counts.unassign_modal'
const EVENTS_T_PREFIX = 'cycle_counts.events'

const collectCycleCountIds = batches => {
  const ids = []
  batches.forEach(batch => {
    const locations = batch?.locations || []
    locations.forEach(loc => {
      if (loc?.id != null) ids.push(loc.id)
    })
  })
  return [...new Set(ids)]
}

// resolveCycleCountIds (pool mode): the unassigned batch row only ever carries its first 100
// locations, so unassigning "the whole pool" walks the paged endpoint instead of the capped
// list the row happens to show — same pattern as ReassignCycleCountDialog.
export const UnassignCycleCountDialog = ({ open, warehouseId, selectedBatches = [], resolveCycleCountIds, onSuccess = () => {}, onCancel = () => {} }) => {
  const { i18n } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [poolIds, setPoolIds] = useState(null)
  const [poolLoading, setPoolLoading] = useState(false)
  const [poolFallback, setPoolFallback] = useState(false)
  const [poolError, setPoolError] = useState(null)
  const [chunkProgress, setChunkProgress] = useState(null)
  // Guards every setState that follows an await against firing after the dialog
  // component itself unmounts (a route change while a pool walk or chunk is in flight).
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!open) {
      setBusy(false)
      setErrorMessage(null)
      setChunkProgress(null)
    }
  }, [open])

  const mapSubmitError = error => {
    const err = error.response?.data?.error
    // CycleCountUnassigner never raises a coded ExpectedError — only an
    // uncoded "Cycle counts in non-allowed state: …" — so there is nothing
    // for localizeReviewError to translate here; show the server message as-is.
    return typeof err === 'string' ? err : err?.message || i18n.t(`${T_PREFIX}.error`)
  }

  useEffect(() => {
    if (!open || !resolveCycleCountIds) {
      setPoolIds(null)
      setPoolLoading(false)
      setPoolFallback(false)
      setPoolError(null)
      return
    }
    let cancelled = false
    setPoolLoading(true)
    setPoolFallback(false)
    setPoolError(null)
    resolveCycleCountIds()
      .then(ids => {
        if (cancelled) return
        setPoolIds([...new Set(ids)])
        setPoolLoading(false)
      })
      .catch(error => {
        if (cancelled) return
        // Only an older platform without the pool endpoint falls back to what the
        // capped row already shows — any other failure has no safe id list to use.
        if (error?.response?.status === 404) {
          setPoolIds(collectCycleCountIds(selectedBatches))
          setPoolFallback(true)
        } else {
          setPoolError(mapSubmitError(error))
        }
        setPoolLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resolveCycleCountIds])

  const cycleCountIds = useMemo(
    () => (resolveCycleCountIds ? (poolIds ?? []) : collectCycleCountIds(selectedBatches)),
    [resolveCycleCountIds, poolIds, selectedBatches],
  )

  // 'submitted' rows carry a recorded counter result too (awaiting reviewer assignment),
  // so they need the same "count will be deleted" warning as 'review_pending' ones.
  const hasReviewPending = useMemo(() => selectedBatches.some(b => ['submitted', 'review_pending'].includes(b?.status)), [selectedBatches])

  // Unassigning an unassigned batch deletes those counts outright (no assignee
  // to detach) — same for a synthetic 'pending' batch with no operator, which
  // is how the detail panel represents its unassigned pool rows.
  const hasUnassigned = useMemo(() => selectedBatches.some(b => b?.status === 'unassigned' || (b?.status === 'pending' && !b?.operator)), [selectedBatches])

  const canSubmit = cycleCountIds.length > 0 && !busy && !(resolveCycleCountIds && (poolLoading || poolError))

  // Mirrors ReassignCycleCountDialog's submitPool: sequential chunks so a chunk failure stops
  // the loop but keeps whatever already succeeded, reported THROUGH onSuccess (the dialog
  // closes right after onSuccess fires) rather than left in local state that would never render.
  const submitPool = async () => {
    const chunks = chunk(cycleCountIds, 500)
    let doneIds = 0
    let totalUnassigned = 0
    for (const ids of chunks) {
      if (!mountedRef.current) return
      setChunkProgress({ done: doneIds, total: cycleCountIds.length })
      try {
        const result = await unassignCycleCounts({ warehouse_id: warehouseId, cycle_count_ids: ids })
        if (!mountedRef.current) return
        totalUnassigned += result?.unassigned_count ?? 0
        doneIds += ids.length
      } catch (error) {
        if (!mountedRef.current) return
        setBusy(false)
        setChunkProgress(null)
        const message = `${i18n.t(`${EVENTS_T_PREFIX}.pool.unassign_partial`, { done: doneIds, total: cycleCountIds.length })} ${mapSubmitError(error)}`
        onSuccess({ unassigned_count: totalUnassigned, partial: true, message })
        return
      }
    }
    if (!mountedRef.current) return
    setBusy(false)
    setChunkProgress(null)
    onSuccess({ unassigned_count: totalUnassigned })
  }

  const onSubmit = async () => {
    if (cycleCountIds.length === 0) return
    setBusy(true)
    setErrorMessage(null)
    // An assigned batch is rendered whole (never truncated) and a take-over can make it larger
    // than the server's 500-id cap, so anything above the cap also goes through the chunked path.
    if (resolveCycleCountIds || cycleCountIds.length > 500) {
      await submitPool()
      return
    }
    try {
      const result = await unassignCycleCounts({
        warehouse_id: warehouseId,
        cycle_count_ids: cycleCountIds,
      })
      setBusy(false)
      onSuccess(result)
    } catch (error) {
      setBusy(false)
      setErrorMessage(mapSubmitError(error))
    }
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={() => {}}>
        <div className="flex items-center justify-center min-h-screen">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative bg-white rounded-lg shadow-xl sm:max-w-lg sm:w-full p-6 text-gray-600 my-3 overflow-hidden">
              <Loader show={busy || poolLoading} />
              {!busy && (
                <div className="absolute top-0 right-0 p-5 cursor-pointer" onClick={onCancel}>
                  <XIcon className="h-5 w-5" />
                </div>
              )}

              <Dialog.Title className="text-lg text-center mb-2">{i18n.t(`${T_PREFIX}.title`)}</Dialog.Title>

              <p className="text-sm text-center text-gray-500 mb-4">
                {resolveCycleCountIds
                  ? i18n.t(`${EVENTS_T_PREFIX}.pool.unassign_scope`, { count: cycleCountIds.length })
                  : i18n.t(`${T_PREFIX}.summary`, {
                      batches: selectedBatches.length,
                      locations: cycleCountIds.length,
                    })}
              </p>

              {resolveCycleCountIds && poolLoading && <p className="text-xs text-center text-gray-500 mb-3">{i18n.t(`${EVENTS_T_PREFIX}.pool.resolving`)}</p>}

              {resolveCycleCountIds && poolFallback && (
                <p className="text-xs text-center text-amber-700 mb-3">
                  {i18n.t(`${EVENTS_T_PREFIX}.pool.unassign_fallback`, { count: cycleCountIds.length })}
                </p>
              )}

              {resolveCycleCountIds && poolError && <p className="text-sm text-red-600 mb-3 text-center">{poolError}</p>}

              {busy && chunkProgress && (
                <p className="text-xs text-center text-gray-500 mb-3">{i18n.t(`${EVENTS_T_PREFIX}.pool.unassigning_chunks`, chunkProgress)}</p>
              )}

              {hasUnassigned && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded">
                  <ExclamationIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{i18n.t(`${T_PREFIX}.unassigned_warning`)}</p>
                </div>
              )}

              {hasReviewPending && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded">
                  <ExclamationIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{i18n.t(`${T_PREFIX}.review_pending_warning`)}</p>
                </div>
              )}

              {selectedBatches.length > 0 && (
                <div className="max-h-56 overflow-y-auto border border-gray-200 rounded mb-4">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.operator`)}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.locations`)}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.status`)}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedBatches.map(batch => (
                        <tr key={batch.id}>
                          <td className="px-3 py-1 text-sm text-gray-900">{batch.operator?.full_name || batch.operator?.email || '-'}</td>
                          <td className="px-3 py-1 text-sm text-gray-900">{batch.total_locations ?? batch.locations?.length ?? 0}</td>
                          <td className="px-3 py-1 text-sm text-gray-500">{i18n.t(`${T_PREFIX}.status_${batch.status}`, batch.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {errorMessage && <p className="text-sm text-red-600 mb-2 text-center">{errorMessage}</p>}

              <div className="mt-3 flex justify-between">
                <Button className="m-1" type="secondary" onClick={onCancel} disabled={busy}>
                  {i18n.t(`${T_PREFIX}.cancel`)}
                </Button>
                <Button className="m-1" type="danger" disabled={!canSubmit} onClick={onSubmit} loading={busy}>
                  {i18n.t(`${T_PREFIX}.confirm`)}
                </Button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
